import axios from 'src/utils/axios';
import parse from 'src/utils/parse';
import logger from 'src/utils/logger';
import type { WazuhPageParams, WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';
import type {
  WazuhEndpointsListData,
  WazuhEndpointsListQuery,
  WazuhEndpointsSummaryData,
  WazuhEndpointCriticalVulnerabilitiesData,
  WazuhEndpointFailedComplianceData,
  WazuhEndpointHealthData,
  WazuhEndpointMetadataData,
  WazuhEndpointSecurityOverviewData,
  WazuhEndpointSecurityOverviewPeriod,
  WazuhEndpointSecurityScoreData,
  WazuhEndpointSnapshotData,
} from 'src/types/mindtime/wazuhEndpoints';

class WazuhEndpointsApi {
  private appendQuery(url: string, params?: Record<string, unknown>): string {
    if (!params) return url;
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        qs.set(key, String(value));
      }
    });
    const query = qs.toString();
    if (!query) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${query}`;
  }

  private getBaseUrl(): string {
    const baseUrl = process.env.REACT_APP_WAZUH_SERVICE;
    if (!baseUrl) {
      throw new Error('REACT_APP_WAZUH_SERVICE environment variable is not defined');
    }
    return baseUrl;
  }

  private getDetail<T extends object>(
    systemWazuhAgentId: string,
    path: string,
    errorLabel: string,
    params?: Record<string, unknown>
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      let baseUrl = '';
      try {
        baseUrl = this.getBaseUrl();
      } catch (error) {
        logger(error, 'error');
        reject(error);
        return;
      }

      const id = systemWazuhAgentId.trim();
      const url = this.appendQuery(
        `${baseUrl}/api/v1/endpoints/${encodeURIComponent(id)}/${path}`,
        params
      );

      axios
        .get(url)
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const body = parsedResponse?.data;
            const data = body?.data as T | undefined;
            if (!data || typeof data !== 'object') {
              throw new Error(`Invalid response: missing endpoint ${errorLabel}`);
            }
            resolve({ ...data, meta: body?.meta });
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  private getPagedDetail<T extends { items?: unknown }>(
    systemWazuhAgentId: string,
    path: string,
    errorLabel: string,
    params?: WazuhPageParams
  ): Promise<T> {
    return this.getDetail<T>(systemWazuhAgentId, path, errorLabel, {
      page: params?.page ?? 0,
      pageSize: params?.pageSize ?? 10,
    }).then((data) => {
      if (!Array.isArray(data.items)) {
        throw new Error(`Invalid response: ${errorLabel} must contain items array`);
      }
      return data;
    });
  }

  async list(params?: WazuhEndpointsListQuery): Promise<WazuhEndpointsListData> {
    return new Promise((resolve, reject) => {
      let baseUrl = '';
      try {
        baseUrl = this.getBaseUrl();
      } catch (error) {
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(this.appendQuery(`${baseUrl}/api/v1/endpoints`, params))
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data?.data as WazuhEndpointsListData | undefined;
            if (!data || !Array.isArray(data.items)) {
              throw new Error('Invalid response: endpoints list must contain items array');
            }
            resolve(data);
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getSummary(
    params?: WazuhScopeParams & { wazuhAgentId?: string }
  ): Promise<WazuhEndpointsSummaryData> {
    return new Promise((resolve, reject) => {
      let baseUrl = '';
      try {
        baseUrl = this.getBaseUrl();
      } catch (error) {
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(this.appendQuery(`${baseUrl}/api/v1/endpoints/summary`, params))
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data?.data as WazuhEndpointsSummaryData | undefined;
            if (!data || typeof data !== 'object') {
              throw new Error('Invalid response: missing endpoints summary data');
            }
            resolve(data);
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  getSnapshot(systemWazuhAgentId: string): Promise<WazuhEndpointSnapshotData> {
    return this.getDetail(systemWazuhAgentId, 'snapshot', 'snapshot data');
  }

  getMetadata(systemWazuhAgentId: string): Promise<WazuhEndpointMetadataData> {
    return this.getDetail(systemWazuhAgentId, 'metadata', 'metadata');
  }

  getSecurityScore(systemWazuhAgentId: string): Promise<WazuhEndpointSecurityScoreData> {
    return this.getDetail(systemWazuhAgentId, 'security-score', 'security score data');
  }

  getSecurityOverview(
    systemWazuhAgentId: string,
    period: WazuhEndpointSecurityOverviewPeriod
  ): Promise<WazuhEndpointSecurityOverviewData> {
    return this.getDetail(systemWazuhAgentId, 'security-overview', 'security overview data', {
      period,
    });
  }

  getHealth(systemWazuhAgentId: string): Promise<WazuhEndpointHealthData> {
    return this.getDetail(systemWazuhAgentId, 'health', 'health data');
  }

  getFailedCompliance(
    systemWazuhAgentId: string,
    params?: WazuhPageParams
  ): Promise<WazuhEndpointFailedComplianceData> {
    return this.getPagedDetail(
      systemWazuhAgentId,
      'failed-compliance',
      'failed-compliance data',
      params
    );
  }

  getCriticalVulnerabilities(
    systemWazuhAgentId: string,
    params?: WazuhPageParams
  ): Promise<WazuhEndpointCriticalVulnerabilitiesData> {
    return this.getPagedDetail(
      systemWazuhAgentId,
      'critical-vulnerabilities',
      'critical-vulnerabilities data',
      params
    );
  }

  async restart(systemWazuhAgentId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      let baseUrl = '';
      try {
        baseUrl = this.getBaseUrl();
      } catch (error) {
        logger(error, 'error');
        reject(error);
        return;
      }

      const id = systemWazuhAgentId.trim();
      if (!id) {
        reject(new Error('Missing system Wazuh agent id'));
        return;
      }

      axios
        .put(`${baseUrl}/api/v1/endpoints/${encodeURIComponent(id)}/restart`)
        .then(() => resolve())
        .catch((error) => {
          logger(error, 'error');
          const apiMessage = error?.response?.data?.message;
          reject(
            new Error(
              typeof apiMessage === 'string' && apiMessage.trim()
                ? apiMessage.trim()
                : error instanceof Error
                  ? error.message
                  : 'Failed to restart agent'
            )
          );
        });
    });
  }
}

export const mindtimeWazuhEndpointsApi = new WazuhEndpointsApi();
